import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import Image from "next/image";
import { useXtdhGrantQuery } from "@/hooks/useXtdhGrantQuery";
import { GrantedListSkeleton } from "@/components/user/xtdh/granted-list/subcomponents/GrantedListSkeleton";
import { GrantedListError } from "@/components/user/xtdh/granted-list/subcomponents/GrantedListError";
import { GrantListItemContainer, StatusBadge, GrantExpiryBadge, GrantDetailsRow, GrantTokensPanel } from "@/components/user/xtdh/granted-list/subcomponents/UserPageXtdhGrantListItem/subcomponents";
import { mapTokenCountToState, getContractAddress, mapGrantChainToSupportedChain } from "@/components/user/xtdh/granted-list/subcomponents/UserPageXtdhGrantListItem/formatters";
import { formatAmount, formatDateTime, formatTdhRatePerToken, getTargetTokensCountInfo } from "@/components/user/xtdh/utils/xtdhGrantFormatters";
import { shortenAddress } from "@/helpers/address.helpers";
interface XtdhGrantDetailsPanelProps {
  readonly grantId: string;
}
export function XtdhGrantDetailsPanel(t0) {
  const $ = _c(97);
  const {
    grantId
  } = t0;
  let t1;
  if ($[0] !== grantId) {
    t1 = {
      grantId
    };
    $[0] = grantId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    grant,
    isLoading,
    isError,
    errorMessage,
    refetch
  } = useXtdhGrantQuery(t1);
  let t2;
  bb0: {
    if (!grant) {
      t2 = null;
      break bb0;
    }
    let t3;
    if ($[2] !== grant.target_contract) {
      t3 = getContractAddress(grant.target_contract);
      $[2] = grant.target_contract;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    const contractAddress = t3;
    let chain = null;
    try {
      let t4;
      if ($[4] !== grant.target_chain) {
        t4 = mapGrantChainToSupportedChain(grant.target_chain);
        $[4] = grant.target_chain;
        $[5] = t4;
      } else {
        t4 = $[5];
      }
      chain = t4;
    } catch {}
    let t4;
    if ($[6] !== grant.target_tokens_count) {
      t4 = mapTokenCountToState(grant.target_tokens_count);
      $[6] = grant.target_tokens_count;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    const tokenState = t4;
    let rateLabel;
    let t5;
    let tokensCountInfo;
    if ($[8] !== grant.rate || $[9] !== grant.target_tokens_count) {
      tokensCountInfo = getTargetTokensCountInfo(grant.target_tokens_count ?? null);
      const tokensCountValue = typeof tokensCountInfo.count === "number" ? tokensCountInfo.count : null;
      let t6;
      if ($[13] !== grant.rate) {
        t6 = formatAmount(grant.rate);
        $[13] = grant.rate;
        $[14] = t6;
      } else {
        t6 = $[14];
      }
      rateLabel = t6;
      t5 = formatTdhRatePerToken(grant.rate, tokensCountValue);
      $[8] = grant.rate;
      $[9] = grant.target_tokens_count;
      $[10] = rateLabel;
      $[11] = t5;
      $[12] = tokensCountInfo;
    } else {
      rateLabel = $[10];
      t5 = $[11];
      tokensCountInfo = $[12];
    }
    const ratePerTokenLabel = t5;
    let t6;
    if ($[15] !== tokensCountInfo.kind || $[16] !== tokensCountInfo.label) {
      t6 = () => {
        if (tokensCountInfo.kind === "all") {
          return "all tokens in this collection";
        }
        if (tokensCountInfo.kind === "count") {
          return `${tokensCountInfo.label} tokens granted`;
        }
        return "an unknown number of tokens";
      };
      $[15] = tokensCountInfo.kind;
      $[16] = tokensCountInfo.label;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    const tokensDescription = t6();
    const ratePerTokenHint = ratePerTokenLabel ? `${rateLabel} total TDH ÷ ${tokensDescription}` : null;
    const t7 = chain;
    const t8 = grant.valid_from ?? null;
    let t9;
    if ($[18] !== t8) {
      t9 = formatDateTime(t8, {
        fallbackLabel: "Immediately"
      });
      $[18] = t8;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    const t10 = grant.valid_to ?? null;
    let t11;
    if ($[20] !== t10) {
      t11 = formatDateTime(t10);
      $[20] = t10;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    let t12;
    if ($[22] !== chain || $[23] !== contractAddress || $[24] !== rateLabel || $[25] !== ratePerTokenHint || $[26] !== ratePerTokenLabel || $[27] !== t11 || $[28] !== t9 || $[29] !== tokenState || $[30] !== tokensCountInfo.label) {
      t12 = {
        chain: t7,
        contractAddress,
        tokenState,
        validFromLabel: t9,
        validUntilLabel: t11,
        tokensCountLabel: tokensCountInfo.label,
        rateLabel,
        ratePerTokenLabel,
        ratePerTokenHint
      };
      $[22] = chain;
      $[23] = contractAddress;
      $[24] = rateLabel;
      $[25] = ratePerTokenHint;
      $[26] = ratePerTokenLabel;
      $[27] = t11;
      $[28] = t9;
      $[29] = tokenState;
      $[30] = tokensCountInfo.label;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    t2 = t12;
  }
  const viewModel = t2;
  if (isLoading) {
    let t3;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <GrantedListSkeleton />;
      $[32] = t3;
    } else {
      t3 = $[32];
    }
    return t3;
  }
  if (isError || !grant || !viewModel) {
    const t3 = errorMessage ?? "Failed to load grant details.";
    let t4;
    if ($[33] !== refetch) {
      t4 = () => refetch();
      $[33] = refetch;
      $[34] = t4;
    } else {
      t4 = $[34];
    }
    let t5;
    if ($[35] !== t3 || $[36] !== t4) {
      t5 = <GrantedListError message={t3} onRetry={t4} />;
      $[35] = t3;
      $[36] = t4;
      $[37] = t5;
    } else {
      t5 = $[37];
    }
    return t5;
  }
  const grantor = grant.grantor;
  const grantorHandle = grantor.handle;
  let t3;
  if ($[38] !== grantor.primary_address || $[39] !== grantorHandle) {
    t3 = grantorHandle ?? shortenAddress(grantor.primary_address) ?? "Unknown grantor";
    $[38] = grantor.primary_address;
    $[39] = grantorHandle;
    $[40] = t3;
  } else {
    t3 = $[40];
  }
  const displayHandle = t3;
  let t4;
  if ($[41] !== grantor.primary_address) {
    t4 = shortenAddress(grantor.primary_address);
    $[41] = grantor.primary_address;
    $[42] = t4;
  } else {
    t4 = $[42];
  }
  const displayAddress = t4;
  let t5;
  if ($[43] !== displayHandle || $[44] !== grantor.pfp) {
    t5 = <div className="tw-relative tw-h-14 tw-w-14 tw-overflow-hidden tw-rounded-lg tw-bg-iron-800">{grantor.pfp ? <Image src={grantor.pfp} alt={displayHandle} fill={true} sizes="56px" className="tw-h-full tw-w-full tw-object-cover" /> : <div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-text-xs tw-font-semibold tw-text-iron-400">?</div>}</div>;
    $[43] = displayHandle;
    $[44] = grantor.pfp;
    $[45] = t5;
  } else {
    t5 = $[45];
  }
  let t6;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="tw-text-xs tw-font-semibold tw-uppercase tw-text-iron-500">Grantor</div>;
    $[46] = t6;
  } else {
    t6 = $[46];
  }
  let t7;
  if ($[47] !== displayHandle) {
    t7 = <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-iron-100">{displayHandle}</p>;
    $[47] = displayHandle;
    $[48] = t7;
  } else {
    t7 = $[48];
  }
  let t8;
  if ($[49] !== displayAddress || $[50] !== grantorHandle) {
    t8 = grantorHandle && <p className="tw-m-0 tw-text-xs tw-text-iron-400">{displayAddress}</p>;
    $[49] = displayAddress;
    $[50] = grantorHandle;
    $[51] = t8;
  } else {
    t8 = $[51];
  }
  let t9;
  if ($[52] !== t7 || $[53] !== t8) {
    t9 = <div className="tw-flex tw-flex-col tw-gap-1">{t6}{t7}{t8}</div>;
    $[52] = t7;
    $[53] = t8;
    $[54] = t9;
  } else {
    t9 = $[54];
  }
  let t10;
  if ($[55] !== t5 || $[56] !== t9) {
    t10 = <div className="tw-flex tw-items-start tw-gap-3">{t5}{t9}</div>;
    $[55] = t5;
    $[56] = t9;
    $[57] = t10;
  } else {
    t10 = $[57];
  }
  let t11;
  if ($[58] !== grant.valid_to) {
    t11 = <GrantExpiryBadge validUntil={grant.valid_to} />;
    $[58] = grant.valid_to;
    $[59] = t11;
  } else {
    t11 = $[59];
  }
  let t12;
  if ($[60] !== grant.status || $[61] !== grant.valid_from || $[62] !== grant.valid_to) {
    t12 = <StatusBadge status={grant.status} validFrom={grant.valid_from} validTo={grant.valid_to} />;
    $[60] = grant.status;
    $[61] = grant.valid_from;
    $[62] = grant.valid_to;
    $[63] = t12;
  } else {
    t12 = $[63];
  }
  let t13;
  if ($[64] !== t11 || $[65] !== t12) {
    t13 = <div className="tw-flex tw-items-center tw-gap-3">{t11}{t12}</div>;
    $[64] = t11;
    $[65] = t12;
    $[66] = t13;
  } else {
    t13 = $[66];
  }
  let t14;
  if ($[67] !== t10 || $[68] !== t13) {
    t14 = <header className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-4">{t10}{t13}</header>;
    $[67] = t10;
    $[68] = t13;
    $[69] = t14;
  } else {
    t14 = $[69];
  }
  let t15;
  if ($[70] !== viewModel.tokensCountLabel) {
    t15 = <GrantDetailsRow label="Tokens granted" value={viewModel.tokensCountLabel} />;
    $[70] = viewModel.tokensCountLabel;
    $[71] = t15;
  } else {
    t15 = $[71];
  }
  let t16;
  if ($[72] !== viewModel.rateLabel) {
    t16 = <span>{viewModel.rateLabel}</span>;
    $[72] = viewModel.rateLabel;
    $[73] = t16;
  } else {
    t16 = $[73];
  }
  let t17;
  if ($[74] !== viewModel.ratePerTokenHint || $[75] !== viewModel.ratePerTokenLabel) {
    t17 = viewModel.ratePerTokenLabel ? <span className="tw-text-xs tw-font-semibold tw-text-iron-400 tw-whitespace-nowrap" title={viewModel.ratePerTokenHint ?? undefined}>({viewModel.ratePerTokenLabel}/token)</span> : null;
    $[74] = viewModel.ratePerTokenHint;
    $[75] = viewModel.ratePerTokenLabel;
    $[76] = t17;
  } else {
    t17 = $[76];
  }
  let t18;
  if ($[77] !== t16 || $[78] !== t17) {
    t18 = <GrantDetailsRow label="TDH rate" value={<div className="tw-flex tw-items-baseline tw-gap-2 tw-text-sm tw-font-medium tw-text-iron-100">{t16}{t17}</div>} />;
    $[77] = t16;
    $[78] = t17;
    $[79] = t18;
  } else {
    t18 = $[79];
  }
  let t19;
  if ($[80] !== viewModel.validFromLabel) {
    t19 = <GrantDetailsRow label="Valid from" value={viewModel.validFromLabel} />;
    $[80] = viewModel.validFromLabel;
    $[81] = t19;
  } else {
    t19 = $[81];
  }
  let t20;
  if ($[82] !== t15 || $[83] !== t18 || $[84] !== t19) {
    t20 = <dl className="tw-grid tw-gap-3 sm:tw-grid-cols-2 lg:tw-grid-cols-3">{t15}{t18}{t19}</dl>;
    $[82] = t15;
    $[83] = t18;
    $[84] = t19;
    $[85] = t20;
  } else {
    t20 = $[85];
  }
  let t21;
  if ($[86] !== t14 || $[87] !== t20) {
    t21 = <div className="tw-flex tw-flex-col tw-gap-4">{t14}{t20}</div>;
    $[86] = t14;
    $[87] = t20;
    $[88] = t21;
  } else {
    t21 = $[88];
  }
  const t22 = viewModel.contractAddress ?? null;
  let t23;
  if ($[89] !== grant.id || $[90] !== t22 || $[91] !== viewModel.chain || $[92] !== viewModel.tokenState) {
    t23 = <GrantTokensPanel chain={viewModel.chain} contractAddress={t22} grantId={grant.id} state={viewModel.tokenState} />;
    $[89] = grant.id;
    $[90] = t22;
    $[91] = viewModel.chain;
    $[92] = viewModel.tokenState;
    $[93] = t23;
  } else {
    t23 = $[93];
  }
  let t24;
  if ($[94] !== t21 || $[95] !== t23) {
    t24 = <GrantListItemContainer>{t21}{t23}</GrantListItemContainer>;
    $[94] = t21;
    $[95] = t23;
    $[96] = t24;
  } else {
    t24 = $[96];
  }
  return t24;
}